import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { updateBugoBrand } from '@shared/api/bugoBrand/bugoBrand.controller';
import { bugoBrandListHookUrl } from '@shared/api/bugoBrand/bugoBrand.hook';
import { BugoBrandFeatureFlags } from '@shared/api/bugoBrand/bugoBrand.interface';
import { bugoBrandListStateSelector } from '@shared/api/bugoBrand/bugoBrand.selector';
import { Loading } from '@shared/components/Loading';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { LowerRole } from '@shared/types';
import { Switch } from 'antd';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { useAdminTitleHook } from '../hooks/useAdminTitleHook';

const AdminBugoBrandFeatureEditPage: FC = function AdminBugoBrandFeatureEditPage() {
  useAdminTitleHook('기능 수정');

  const { bugoBrandId } = useParams();
  const navigate = useNavigate();
  const notificationInstance = useRecoilValue(notificationInstanceAtom);
  const bugoBrandListState = useRecoilValue(bugoBrandListStateSelector);
  const queryClient = useQueryClient();

  const bugoBrand = useMemo(() => {
    if (bugoBrandListState.status === 'success' && bugoBrandListState.data) {
      return bugoBrandListState.data.find((item) => item._id === bugoBrandId);
    }
  }, [bugoBrandId, bugoBrandListState.data, bugoBrandListState.status]);

  const [featureFlags, setFeatureFlags] = useState<BugoBrandFeatureFlags | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const currentFlags = useMemo(() => {
    if (featureFlags !== undefined) return featureFlags;
    return bugoBrand?.featureFlags ?? {};
  }, [featureFlags, bugoBrand?.featureFlags]);

  const handleToggle = useCallback(
    (key: keyof BugoBrandFeatureFlags, value: boolean) => {
      setFeatureFlags((prev) => ({
        ...prev,
        ...currentFlags,
        [key]: value,
      }));
    },
    [currentFlags],
  );

  const handleSubmit = useCallback(async () => {
    if (!bugoBrandId) return;
    setIsLoading(true);
    try {
      await updateBugoBrand(
        {
          _id: bugoBrandId,
          featureFlags: {
            ...currentFlags,
            ...featureFlags,
          },
        },
        LowerRole.Admin,
      );
      notificateSuccess(notificationInstance, '기능 설정 저장 완료');
      queryClient.invalidateQueries(bugoBrandListHookUrl);
      navigate(-1);
    } catch {
      notificateError(notificationInstance, '기능 설정 저장 실패');
    } finally {
      setIsLoading(false);
    }
  }, [
    bugoBrandId,
    currentFlags,
    featureFlags,
    notificationInstance,
    queryClient,
    navigate,
  ]);

  if (!bugoBrand) {
    return (
      <div className="center-box">
        <Loading />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl p-4">
      <h2 className="mb-6 text-lg font-bold">
        {bugoBrand.bugoAgency?.corpName ?? ''} - 기능 설정
      </h2>

      <div className="flex flex-col gap-4 rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold">답례품 판매</p>
            <p className="text-sm text-gray-500">답례품 판매 허용 여부를 설정합니다</p>
          </div>
          <Switch
            checked={currentFlags.giftEnabled ?? false}
            onChange={(checked) => handleToggle('giftEnabled', checked)}
          />
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <button
          className="rounded border px-4 py-2 text-sm hover:bg-gray-50"
          onClick={() => navigate(-1)}
        >
          취소
        </button>
        <button
          className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? '저장 중...' : '저장'}
        </button>
      </div>
    </div>
  );
};

export default AdminBugoBrandFeatureEditPage;
